import { observer } from 'mobx-react-lite';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  CollapsibleSection,
  ComingSoonSetting,
  PageLayout,
  SkillLevelsEditor,
} from '../components/shared';
import { InvitationLinkSection } from '../features/telegram';
import { isUserAdmin } from '../lib/userUtils';
import { useNavigationService, useUserStore } from '../stores';

const Settings: React.FC = observer(() => {
  const params = useParams();
  const { workspaceSlug } = params;
  const { t, i18n } = useTranslation();
  const userStore = useUserStore();
  const navigationService = useNavigationService();

  const languages = [
    { code: 'en', label: '🇺🇸', name: 'English' },
    { code: 'ru', label: '🇷🇺', name: 'Русский' },
  ];

  const isAdmin = userStore.user?.roles
    ? isUserAdmin(userStore.user.roles)
    : false;
  const currentLanguage = i18n.language;

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  // Don't render if workspace slug is not available yet
  if (!workspaceSlug) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 pb-20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading workspace...</p>
        </div>
      </div>
    );
  }

  return (
    <PageLayout
      title={t('settings.title')}
      subtitle={t('settings.description')}
      icon="⚙️"
    >
      <Card className="shadow-xl">
        <CardContent className="p-6 space-y-4">
          {/* General Settings Section */}
          <CollapsibleSection
            title={t('settings.general.title')}
            description={t('settings.general.description')}
            icon="⚙️"
            defaultExpanded={false}
          >
            <div className="space-y-4">
              {/* Language Settings */}
              <div>
                <h4 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
                  <span className="text-lg mr-2">🌐</span>
                  {t('settings.general.language.title')}
                </h4>
                <p className="text-xs text-gray-500 mb-3">
                  {t('settings.general.language.description')}
                </p>

                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      {t('settings.general.language.chooseLanguage')}
                    </label>
                    <div className="flex space-x-2">
                      {languages.map((lang) => (
                        <button
                          key={lang.code}
                          onClick={() => changeLanguage(lang.code)}
                          className={`
                            flex items-center space-x-2 px-3 py-2 rounded-lg text-xs transition-all duration-200 border min-w-[100px]
                            ${
                              currentLanguage === lang.code
                                ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white border-transparent shadow-lg'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400'
                            }
                          `}
                        >
                          <span className="text-sm">{lang.label}</span>
                          <span className="font-medium">{lang.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Profile Settings */}
              <div>
                <h4 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
                  <span className="text-lg mr-2">👤</span>
                  {t('settings.general.profile.title')}
                </h4>
                <p className="text-xs text-gray-500 mb-3">
                  {t('settings.general.profile.description')}
                </p>

                <button
                  onClick={() => navigationService.toProfile(workspaceSlug!)}
                  className="flex items-center justify-between w-full p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-600 text-sm">👤</span>
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-gray-900 text-sm">
                        {t('profile.editProfile')}
                      </p>
                      <p className="text-xs text-gray-500">
                        {t('profile.editProfileDescription')}
                      </p>
                    </div>
                  </div>
                  <svg
                    className="w-4 h-4 text-gray-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </CollapsibleSection>

          {/* Telegram Notifications Section */}
          <CollapsibleSection
            title={t('settings.telegram.title')}
            description={t('settings.telegram.description')}
            icon="📱"
            defaultExpanded={false}
          >
            <div className="space-y-6">
              {isAdmin ? (
                <div>
                  <div className="space-y-3">
                    <ComingSoonSetting
                      title={t('settings.telegram.admin.cancellations.title')}
                      description={t(
                        'settings.telegram.admin.cancellations.description'
                      )}
                      icon="❌"
                      comingSoonText={t('settings.telegram.comingSoon')}
                      showToggle={true}
                      toggleChecked={true}
                    />
                    <ComingSoonSetting
                      title={t('settings.telegram.admin.negativeBalance.title')}
                      description={t(
                        'settings.telegram.admin.negativeBalance.description'
                      )}
                      icon="💰"
                      comingSoonText={t('settings.telegram.comingSoon')}
                      showToggle={true}
                      toggleChecked={false}
                    />
                    <ComingSoonSetting
                      title={t(
                        'settings.telegram.admin.scheduleNotifications.title'
                      )}
                      description={t(
                        'settings.telegram.admin.scheduleNotifications.description'
                      )}
                      icon="📅"
                      comingSoonText={t('settings.telegram.comingSoon')}
                      showToggle={true}
                      toggleChecked={true}
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <ComingSoonSetting
                    title={t('settings.telegram.participant.replacement.title')}
                    description={t(
                      'settings.telegram.participant.replacement.description'
                    )}
                    icon="🔄"
                    comingSoonText={t('settings.telegram.comingSoon')}
                    showToggle={true}
                    toggleChecked={false}
                  />
                  <ComingSoonSetting
                    title={t(
                      'settings.telegram.participant.negativeBalance.title'
                    )}
                    description={t(
                      'settings.telegram.participant.negativeBalance.description'
                    )}
                    icon="💰"
                    comingSoonText={t('settings.telegram.comingSoon')}
                    showToggle={true}
                    toggleChecked={false}
                  />
                </div>
              )}
            </div>
          </CollapsibleSection>

          {/* Team Management Section - Only show for admins */}
          {isAdmin && (
            <CollapsibleSection
              title={t('settings.team.title')}
              description={t('settings.team.description')}
              icon="👥"
              defaultExpanded={false}
            >
              <div className="space-y-4">
                {/* Invitation Link Section */}
                <InvitationLinkSection />

                {/* Payment Models */}
                <ComingSoonSetting
                  title={t('settings.team.paymentModels.title')}
                  description={t('settings.team.paymentModels.description')}
                  icon="💰"
                  comingSoonText={t('settings.team.paymentModels.comingSoon')}
                />

                {/* Skill Levels */}
                <SkillLevelsEditor />
              </div>
            </CollapsibleSection>
          )}
        </CardContent>
      </Card>
    </PageLayout>
  );
});

export default Settings;
